import React, { useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

import "./ActivityThread.css";

function parseAvatarValue(value) {
  if (!value) return "";
  if (Array.isArray(value)) {
    const selected = value.find((item) => item?.selected || item?.isSelected) || value[0];
    return parseAvatarValue(selected);
  }
  if (typeof value === "object") return parseAvatarValue(value.url || value.src || value.path || value.location);
  const source = String(value).trim();
  if (!source) return "";
  if ((source.startsWith("{") && source.endsWith("}")) || (source.startsWith("[") && source.endsWith("]"))) {
    try {
      return parseAvatarValue(JSON.parse(source));
    } catch (_error) {
      return "";
    }
  }
  if (/^uploads\//i.test(source)) return `/${source}`;
  return source;
}

export function resolveActivityAvatarSource(message = {}) {
  return parseAvatarValue(
    message.senderAvatarUrl ||
    message.senderProfileImageUrl ||
    message.senderUser?.profilePicture ||
    message.senderUser?.profile_picture ||
    message.senderUser?.avatarUrl ||
    message.senderNetworkMember?.profileImageUrl ||
    message.senderNetworkMember?.avatarUrl ||
    message.author?.avatarUrl ||
    message.member?.profileImageUrl
  );
}

export function getActivityAuthorName(message = {}) {
  const userName = [message.senderUser?.first_name || message.senderUser?.firstName, message.senderUser?.last_name || message.senderUser?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return message.senderDisplayName || message.senderName || userName || message.senderNetworkMember?.displayName ||
    message.senderUser?.displayName || message.senderUser?.email || message.senderNetworkMember?.email || "Elite Core";
}

export function getActivityInitials(name = "") {
  return String(name).trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "EC";
}

function getSenderRole(message) {
  if (message.isInternal) return "Internal note";
  if (message.senderRole) return message.senderRole;
  if (message.senderType === "elite_user") return "Elite User";
  if (message.senderType === "network_member") return message.senderNetworkMember?.companyName || "Network Member";
  if (message.senderType === "provider") return "Provider";
  return "System";
}

function formatFileSize(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentKind(attachment = {}) {
  const source = `${attachment.mimeType || attachment.type || ""} ${attachment.fileName || attachment.name || attachment.url || ""}`.toLowerCase();
  if (source.includes("pdf") || /\.pdf(?:$|[?#])/.test(source)) return "pdf";
  if (source.includes("image/") || /\.(png|jpe?g|gif|webp|svg)(?:$|[?#])/.test(source)) return "image";
  return "file";
}

function ActivityThreadAttachments({ message, attachments, onOpenAttachment }) {
  if (!attachments.length) return null;
  return (
    <div className="activity-thread__attachments">
      {attachments.map((attachment, index) => {
        const kind = attachmentKind(attachment);
        const name = attachment.fileName || attachment.name || `Attachment ${index + 1}`;
        const previewUrl = attachment.thumbnailUrl || attachment.previewUrl || attachment.url;
        return (
          <button
            type="button"
            className={`activity-thread__attachment activity-thread__attachment--${kind}`}
            key={`${attachment.fileId || name}-${index}`}
            onClick={() => onOpenAttachment?.(message, attachment)}
            aria-label={`Open attachment ${name}`}
          >
            {kind === "image" && previewUrl ? (
              <img className="activity-thread__attachment-image" src={previewUrl} alt="" loading="lazy" />
            ) : (
              <span className="activity-thread__attachment-icon" aria-hidden="true">
                {kind === "pdf" ? <PictureAsPdfOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}
              </span>
            )}
            <span className="activity-thread__attachment-copy">
              <span className="activity-thread__attachment-name" title={name}>{name}</span>
              <span className="activity-thread__attachment-meta">
                {[formatFileSize(attachment.fileSize || attachment.size), kind === "pdf" ? "Open PDF" : "Open file"].filter(Boolean).join(" · ")}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MessageBody({ children }) {
  const segments = String(children || "").split(/(https?:\/\/[^\s]+)/g);
  return (
    <Typography component="div" className="activity-thread__body">
      {segments.map((segment, index) => /^https?:\/\//.test(segment) ? (
        <a key={`${segment}-${index}`} href={segment} target="_blank" rel="noopener noreferrer">{segment}</a>
      ) : <React.Fragment key={`${segment}-${index}`}>{segment}</React.Fragment>)}
    </Typography>
  );
}

export function ActivityThreadItem({ message, formatTimestamp, onOpenAttachment }) {
  const authorName = getActivityAuthorName(message);
  const avatarSource = resolveActivityAvatarSource(message);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const attachments = Array.isArray(message.attachmentsJson) ? message.attachmentsJson : [];
  return (
    <article className={`activity-thread__item${message.isRead === false ? " activity-thread__item--unread" : ""}`}>
      <Avatar
        className="activity-thread__avatar"
        src={!avatarFailed ? avatarSource : undefined}
        alt={avatarSource && !avatarFailed ? `${authorName} profile` : ""}
        imgProps={{ onError: () => setAvatarFailed(true) }}
      >
        {getActivityInitials(authorName)}
      </Avatar>
      <div className="activity-thread__content">
        <header className="activity-thread__header">
          <div className="activity-thread__identity">
            <Typography component="h3" className="activity-thread__author">{authorName}</Typography>
            <Typography className="activity-thread__meta">{getSenderRole(message)} · {formatTimestamp?.(message.createdAt) || "Time not available"}</Typography>
          </div>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap className="activity-thread__chips">
            {message.isInternal ? <Chip size="small" variant="outlined" label="Internal" /> : <Chip size="small" variant="outlined" label="Visible to provider" />}
            {message.isRead === false ? <Chip size="small" color="warning" label="Unread" /> : null}
          </Stack>
        </header>
        {message.messageBody ? <MessageBody>{message.messageBody}</MessageBody> : null}
        <ActivityThreadAttachments message={message} attachments={attachments} onOpenAttachment={onOpenAttachment} />
      </div>
    </article>
  );
}

export function ActivityThread({ messages = [], formatTimestamp, onOpenAttachment, emptyTitle = "No messages yet", emptyDescription }) {
  const items = useMemo(() => Array.isArray(messages) ? messages : [], [messages]);
  if (!items.length) {
    return (
      <div className="activity-thread__empty">
        <span className="activity-thread__empty-icon" aria-hidden="true"><ChatBubbleOutlineIcon /></span>
        <Typography component="h3" className="activity-thread__empty-title">{emptyTitle}</Typography>
        <Typography className="activity-thread__empty-description">{emptyDescription || "Start the conversation with the assigned provider or leave an internal note for your team."}</Typography>
      </div>
    );
  }
  return <div className="activity-thread__list">{items.map((message) => <ActivityThreadItem key={message.id} message={message} formatTimestamp={formatTimestamp} onOpenAttachment={onOpenAttachment} />)}</div>;
}

export function ActivityThreadComposer({ onSubmit, onUploadAttachments, sending = false, uploading = false, defaultInternal = true }) {
  const [messageBody, setMessageBody] = useState("");
  const [isInternal, setIsInternal] = useState(defaultInternal);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleSend = async () => {
    if (!messageBody.trim() && !uploadedFiles.length) return;
    await onSubmit?.({ messageBody, isInternal, senderType: "elite_user", attachmentsJson: uploadedFiles });
    setMessageBody("");
    setSelectedFiles([]);
    setUploadedFiles([]);
  };

  return (
    <div className={`activity-thread__composer${isInternal ? " activity-thread__composer--internal" : ""}`}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Message"
        placeholder="Add a message or internal note..."
        value={messageBody}
        onChange={(event) => setMessageBody(event.target.value)}
      />
      {isInternal ? <Typography className="activity-thread__internal-notice">Only internal Elite Core users can see this note.</Typography> : null}
      {selectedFiles.length ? <div className="activity-thread__pending">{selectedFiles.map((file) => <span key={`${file.name}-${file.size}`}>{file.name}</span>)}</div> : null}
      {uploadedFiles.length ? <div className="activity-thread__pending">{uploadedFiles.map((file, index) => <span key={file.fileId || `${file.fileName}-${index}`}>Attached: {file.fileName || `File ${index + 1}`}</span>)}</div> : null}
      <div className="activity-thread__composer-actions">
        <FormControlLabel control={<Checkbox checked={isInternal} onChange={(event) => setIsInternal(event.target.checked)} />} label="Internal note only" />
        {onUploadAttachments ? (
          <Button variant="text" component="label" startIcon={<AttachFileIcon />} disabled={uploading || sending}>
            Attach files
            <input hidden type="file" multiple accept=".pdf,.ai,.eps,.ps,.svg,.png,.jpg,.jpeg,.webp,.gif,.zip" onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))} />
          </Button>
        ) : null}
        {selectedFiles.length ? <Button variant="text" disabled={uploading} onClick={async () => { const response = await onUploadAttachments?.(selectedFiles); setUploadedFiles(Array.isArray(response?.files) ? response.files : []); setSelectedFiles([]); }}>{uploading ? "Uploading..." : "Upload attachments"}</Button> : null}
        <span className="activity-thread__composer-spacer" />
        <Button variant="contained" onClick={handleSend} disabled={sending || (!messageBody.trim() && !uploadedFiles.length)}>{sending ? "Sending..." : isInternal ? "Add internal note" : "Send message"}</Button>
      </div>
    </div>
  );
}

